/**
 * Changes feature data layer (api.md §16 — TEMPORAL, DECISIONS #49).
 *
 * Query keys live under the repo prefix (`['repo', rid, 'changes', …]`) so
 * the sanctioned job-done invalidation (`invalidateAfterJobDone` prefix-
 * invalidates `['repo', rid]`) refreshes the banner/panel/timeline the
 * moment a refresh completes — no polling.
 *
 * A repo with no map history yet answers 404; that is a NORMAL state here
 * (never mapped), surfaced as `null` data so the banner and panel can simply
 * not render instead of erroring.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiGet, apiPost, ApiClientError } from '../../api/client';
import type { ChangesResponse, SnapshotListResponse, Ulid } from '../../api/types';

export const changesKeys = {
  /** GET /api/repos/{rid}/changes[?since=…] */
  changes: (rid: Ulid, since: string | null) => ['repo', rid, 'changes', since] as const,
  /** GET /api/repos/{rid}/changes/snapshots */
  snapshots: (rid: Ulid) => ['repo', rid, 'changes', 'snapshots'] as const,
};

async function getOrNull<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  try {
    return await apiGet<T>(path, signal);
  } catch (e) {
    if (e instanceof ApiClientError && e.status === 404) return null;
    throw e;
  }
}

/**
 * The diff vs the caller's baseline (`since` overrides for the timeline).
 * `null` = no map history yet (unmapped repo) — render nothing.
 */
export function useRepoChanges(
  rid: Ulid | undefined,
  since: string | null = null,
): UseQueryResult<ChangesResponse | null> {
  return useQuery({
    queryKey: changesKeys.changes(rid ?? '', since),
    queryFn: ({ signal }) =>
      getOrNull<ChangesResponse>(
        `/repos/${rid}/changes${since !== null ? `?since=${encodeURIComponent(since)}` : ''}`,
        signal,
      ),
    enabled: rid !== undefined && rid.length > 0,
    staleTime: 30_000,
  });
}

/** The snapshot timeline (Codebase page's Map history). */
export function useSnapshotTimeline(
  rid: Ulid | undefined,
): UseQueryResult<SnapshotListResponse | null> {
  return useQuery({
    queryKey: changesKeys.snapshots(rid ?? ''),
    queryFn: ({ signal }) =>
      getOrNull<SnapshotListResponse>(`/repos/${rid}/changes/snapshots`, signal),
    enabled: rid !== undefined && rid.length > 0,
    staleTime: 30_000,
  });
}

/**
 * POST /changes/seen — the seen-stamp. Called on changes-panel open or
 * banner dismiss ONLY (viewing is seeing; loading the map never stamps).
 * Invalidates the repo's changes queries so the banner drops immediately.
 */
export function useMarkSeen(rid: Ulid) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapshotId: string) =>
      apiPost<void>(`/repos/${rid}/changes/seen`, { snapshot_id: snapshotId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['repo', rid, 'changes'] });
    },
  });
}

/** The banner-visible sum (the retold law: retold NEVER counts). */
export function bannerCount(changes: ChangesResponse): number {
  return changes.counts.code_changed + changes.counts.new + changes.counts.removed;
}
